import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { finesService } from '../services/finesService';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import { contactsService } from '../services/contactsService';
import { findContract } from '../services/contractRecord';
import { passportKey } from '../services/passportKey';
import { contractNotFoundText } from '../services/contractText';
import { USE_CONTRACTS_V2, v2SearchRecords } from '../services/contractsV2';
import { plateOf, carContracts, contractCard } from '../services/carContract';
import { historyTerm, fleetTerm, contractTerm, staffTerm, isGreeting, contractCommand } from './assistantCommands';

/** Said when the customer list itself could not be loaded (not "no customer with ..."). */
export const CONTACTS_UNAVAILABLE = 'Customer list not available - try again in a moment.';

// Engine answers arrive as Telegram-style HTML (<b>, <i>, <code>); everything else is
// shown as text, so only those tags are re-enabled after escaping.
export const assistantHtml = (text) => String(text || '')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/&lt;(\/?)(b|i|code|u|s|pre)&gt;/g, '<$1$2>')
    .replace(/\n/g, '<br/>');

// Words that mean "a data question", not a navigation shortcut.
const ENGINE_WORDS = /[?؟]|\b(how much|how many|who has|if i close|close now|statement|salik|darb|parking|fines?|owe|unpaid|overdue)\b|كام|لو |إزاي|ازاي|مين|ايه|إيه|هل |قفل|سالك|درب|باركينج|مخالف|كشف|متأخر|بتخلص|النهارده|النهاردة|امبارح|الشهر|الاسبوع|الأسبوع|فرع|أعلى|اعلى|قارن/i;

export const useAiAssistant = (stats, currentUser) => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    // Warm the customer-contacts store so typing a phone / contract resolves instantly.
    useEffect(() => { contactsService.ensureLoaded(); }, []);

    const canAsk = hasPermission(PERMISSIONS.ask_assistant);
    // one question at a time on the engine: a 409 (someone else is asking) is retried
    // every 5 s for up to 40 s before it is shown as an error
    const askEngine = async (query) => {
        for (let attempt = 0; ; attempt += 1) {
            try {
                return await finesService.assistantAsk(query.trim());
            } catch (err) {
                if (err?.response?.status === 409 && attempt < 8) {
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                    continue;
                }
                throw err;
            }
        }
    };

    // A name / phone / ID the customer search below will look for (not a question for the engine).
    const needsCustomerSearch = (query) => {
        const raw = String(query || '').trim();
        if (!raw || (canAsk && ENGINE_WORDS.test(raw)) || !hasPermission(PERMISSIONS.search_contracts)) return false;
        const digits = raw.replace(/\D/g, '');
        const phone = /^\+?[\d\s\-()]{7,}$/.test(raw) && digits.length >= 7 && digits.length <= 15;
        const name = /^[\p{L}\s.'-]{3,}$/u.test(raw) && !/\b(find|search|go to|open)\b/i.test(raw.toLowerCase());
        return phone || name || !!passportKey(raw);          // a passport is searched as an identity (owner, 28 Sep 2026)
    };

    // The contracts store (USE_CONTRACTS_V2) keeps no copy of every customer in the browser: a name / phone / ID is
    // searched there first (a slightly misspelt name too), then answered from memory as before; a search that
    // failed is said as "Customer list not available", never as "no customer".
    const processQuery = (query, options = {}) => {
        if (USE_CONTRACTS_V2 && needsCustomerSearch(query)) {
            // a passport is searched as it is kept (N1234567 for "n 1234567"); anything else as typed
            return contactsService.searchStore(passportKey(query) || query.trim()).then((ok) => answerQuery(query, { ...options, contactsOk: ok }));
        }
        return answerQuery(query, options);
    };

    // `contactsOk`: the contacts store is in memory. When it could not be loaded, a phone /
    // name search says so instead of "No customer with ..." (audit ADM-22).
    // Returns the answer, or a Promise of it for a contract number (checked in the stores first).
    const answerQuery = (query, { contactsOk = contactsService.isLoaded() } = {}) => {
        const q = query.toLowerCase().trim();
        // a question for the engine assistant (contract statement, fines, Salik ...)
        if (canAsk && ENGINE_WORDS.test(query)) return { text: '', type: 'engine', options: [] };

        // Helper to return standardized response
        const respond = (text, type = 'text', options = []) => ({ text, type, options });

        // --- Customers: phone / identity / name -> the customer across ALL contracts (every
        // branch, open and closed, 3 years) from the contacts store, then Customer profile.
        const customerProfile = (c) => ({ identity: c.identity, contractNo: c.latest.c, name: c.name, phone: c.phone, branch: c.latest.b });
        const notFound = (term) => {
            const enc = encodeURIComponent(term);
            const options = [];
            if (hasPermission(PERMISSIONS.view_engine)) options.push({ label: 'Search Ejar (The Engine)', action: `/the-engine?tab=customer&q=${enc}` });
            if (canAsk) options.push({ label: 'Ask the assistant', engine: term });
            if (hasPermission(PERMISSIONS.search_contracts)) options.push({ label: 'Monthly contracts', action: `/contracts?search=${enc}` });
            if (hasPermission(PERMISSIONS.view_history)) options.push({ label: 'History', action: `/report-history?search=${enc}` });
            return options;
        };
        // where else a car's number is looked for: the fleet, the history, the contracts
        const plateOptions = (term) => {
            const enc = encodeURIComponent(String(term).trim());
            const options = [];
            if (hasPermission(PERMISSIONS.search_fleet)) options.push({ label: 'Car 🚗 (Fleet)', action: `/fleet?search=${enc}` });
            if (hasPermission(PERMISSIONS.view_history)) options.push({ label: 'History 📜', action: `/report-history?search=${enc}` });
            if (hasPermission(PERMISSIONS.search_contracts)) options.push({ label: 'Contract 📝', action: `/contracts?search=${enc}` });
            return options;
        };
        const customersAnswer = (term, found, what) => {
            // a typo: never open a customer on a guess - offer the closest names
            if (found[0] && found[0].approximate) {
                return {
                    type: 'customers',
                    text: `No customer is called "${term}". Did you mean:`,
                    customers: found.map((c) => ({ ...c, seed: customerProfile(c) })),
                    options: notFound(term),
                };
            }
            if (found.length === 1) {
                const c = found[0];
                return { type: 'customerProfile', seed: customerProfile(c), text: `${c.name || 'Customer'} · ${c.count} contract${c.count === 1 ? '' : 's'}`, options: [] };
            }
            return {
                type: 'customers',
                text: `${found.length}${found.length === 8 ? '+' : ''} customers match ${what} "${term}". Pick one:`,
                customers: found.map((c) => ({ ...c, seed: customerProfile(c) })),
                options: notFound(term),
            };
        };

        // --- 1. Navigation & Search Commands (Explicit) ---
        // These take priority. If user is specific, we just do it.

        // History / Archive Search (anchored: "Harold Smith" is a name, not "old smith" - ADM-16)
        const historySearch = historyTerm(q);
        if (historySearch) {
            const term = historySearch;
            if (hasPermission(PERMISSIONS.view_history)) {
                navigate(`/report-history?search=${encodeURIComponent(term)}`);
                return respond(`📜 Searching Archive/History for "${term}"...`);
            } else {
                return respond("⛔ You don't have permission to view history.");
            }
        }

        // Fleet / Car Search (Explicit)
        // Matches: "find car ...", "search fleet ..."
        const fleetSearch = fleetTerm(q);
        if (fleetSearch) {
            const term = fleetSearch;
            if (hasPermission(PERMISSIONS.search_fleet)) {
                navigate(`/fleet?search=${encodeURIComponent(term)}`);
                return respond(`🚀 Zooming to Fleet Manager to find "${term}"...`);
            }
        }

        // Contracts (Explicit)
        const contractSearch = contractTerm(q);
        if (contractSearch) {
            const term = contractSearch;
            if (hasPermission(PERMISSIONS.search_contracts)) {
                navigate(`/contracts?search=${encodeURIComponent(term)}`);
                return respond(`📝 Searching Contracts for "${term}"...`);
            }
        }

        // Customer names (Latin or Arabic letters): every customer in the contacts store.
        // Nothing found -> fall through (it may be a command or small talk).
        const looksLikeName = /^[\p{L}\s.'-]{3,}$/u.test(query.trim()) && !/\b(find|search|go to|open)\b/i.test(q);
        if (looksLikeName && hasPermission(PERMISSIONS.search_contracts)) {
            const found = contactsOk ? contactsService.findCustomers(query.trim()) : [];
            if (found.length > 0) return customersAnswer(query.trim(), found, 'the name');
            // two or more words that look like a name and match nobody: say so plainly
            // (the assistant would answer with an empty "0 fines" card for a name that does not exist)
            if (query.trim().split(/\s+/).length >= 2) {
                if (!contactsOk) return { text: CONTACTS_UNAVAILABLE, type: 'error', options: [] };
                return respond(`No customer called "${query.trim()}" in the contracts of the last 3 years. Check the spelling, or search further:`, 'choice', notFound(query.trim()));
            }
        }

        // Staff (Explicit)
        const staffSearch = staffTerm(q);
        if (staffSearch) {
            const term = staffSearch;
            if (hasPermission(PERMISSIONS.search_staff)) {
                navigate(`/staff?search=${encodeURIComponent(term)}`);
                return respond(`👥 Looking for staff member "${term}"...`);
            }
        }

        // --- Phone number -> the customer's contract (with or without country code) ---
        const rawTrim = query.trim();
        const digits = rawTrim.replace(/\D/g, '');
        const looksLikePhone = /^\+?[\d\s\-()]{7,}$/.test(rawTrim) && digits.length >= 7 && digits.length <= 15;
        if (looksLikePhone) {
            if (!hasPermission(PERMISSIONS.search_contracts)) return respond("⛔ You don't have permission to search contracts.");
            if (!contactsOk) return { text: CONTACTS_UNAVAILABLE, type: 'error', options: [] };
            // Phone or identity number -> the customer(s) across every contract, then Customer profile
            const found = contactsService.findCustomers(digits);
            if (found.length > 0) return customersAnswer(digits, found, digits.length >= 15 ? 'the ID' : 'the number');
            return respond(`No customer with ${digits} in the contracts of the last 3 years. Search further:`, 'choice', notFound(digits));
        }

        // --- A car's number (74681, CC 74681) -> the contract it is on NOW, first and clearly (owner, 12 Oct 2026: "لو
        // كتبت رقم عربية اول حاجه تظهر العقد المرفوعه عليه بشكل واضح"); where else to look stays under it ---
        const car = USE_CONTRACTS_V2 && hasPermission(PERMISSIONS.search_contracts) ? plateOf(rawTrim) : null;
        if (car) {
            const where = plateOptions(rawTrim);
            return v2SearchRecords(car.number, 'all')
                .then((records) => {
                    const cars = carContracts(rawTrim, records).map((c) => ({ plate: c.plate, open: contractCard(c.open), last: contractCard(c.last) }));
                    if (!cars.length) return respond(`No contract has a car numbered ${rawTrim.toUpperCase()} in the last 3 years. Search further:`, 'choice', where);
                    return { type: 'carContract', text: '', cars, options: where, canEngine: hasPermission(PERMISSIONS.view_engine) };
                })
                .catch(() => respond(`The contracts could not be searched for ${rawTrim.toUpperCase()} - try again, or search further:`, 'choice', where));
        }

        // --- A passport (letters + digits: N1234567, AB1234567, P1234567A) -> the customer by that identity ---
        // A tourist is found by passport (owner, 28 Sep 2026): it read as a contract number and found nothing. The
        // identity first, then the EXACT contract number (never guessed); an Ejar contract number (I2609000D01…,
        // B2609000D04…, C2511ARE…) or an old all-digit one (1943174) is never taken for a passport (passportKey).
        const passport = passportKey(rawTrim);
        if (passport) {
            if (!hasPermission(PERMISSIONS.search_contracts)) return respond("⛔ You don't have permission to search contracts.");
            const found = contactsOk ? contactsService.findCustomers(passport) : [];
            if (found.length > 0) return customersAnswer(passport, found, 'the ID');
            return contractCommand(passport, { find: (no) => findContract(no), canEngine: hasPermission(PERMISSIONS.view_engine) })
                .then((res) => {
                    if (res.type !== 'error' || res.text !== contractNotFoundText(passport)) return res;
                    // no contract by that number either: say both (or that the customer list itself was not there)
                    if (!contactsOk) return { text: CONTACTS_UNAVAILABLE, type: 'error', options: [] };
                    return respond(`No customer with the ID ${passport} and no contract with that number in the last 3 years. Search further:`, 'choice', notFound(passport));
                });
        }

        // --- Contract number -> the contract (Ejar formats: I…/C…/A…/B… + digits) ---
        // The EXACT number is looked up (contacts store, then the contract records by id) before
        // any button is offered; a number found nowhere says "No contract ... found".
        const compact = rawTrim.replace(/\s+/g, '');
        if (/^[a-z]{1,2}\d{5,}[a-z0-9]*$/i.test(compact) && compact.length >= 8) {
            if (!hasPermission(PERMISSIONS.search_contracts)) return respond("⛔ You don't have permission to search contracts.");
            return contractCommand(compact, { find: (no) => findContract(no), canEngine: hasPermission(PERMISSIONS.view_engine) });
        }

        // --- 0. Smart ID Recognition (Ambiguous / Fallback) ---
        // User typed just a number or code. ASK THEM what to do.

        // Car Plate (Digits only)
        if (/^\d{3,5}$/.test(q)) {
            const options = plateOptions(q);
            if (options.length > 0) {
                return respond(`Found ID "${q}". Where should I search?`, 'choice', options);
            } else {
                return respond("⛔ No permissions to search this ID.");
            }
        }

        // Contract ID (Starts with C)
        if (/^c[a-z0-9]{5,}$/i.test(q)) {
            const options = [];
            if (hasPermission(PERMISSIONS.search_contracts)) {
                options.push({ label: 'Active Contracts 📝', action: `/contracts?search=${encodeURIComponent(q)}` });
            }
            if (hasPermission(PERMISSIONS.view_history)) {
                options.push({ label: 'Archive/History 📜', action: `/report-history?search=${encodeURIComponent(q)}` });
            }
            return respond(`Found Contract ID "${q}". Search location?`, 'choice', options);
        }

        // --- 2. Direct Navigation ---
        if (q.includes('go to') || q.includes('open')) {
            if (q.includes('fleet') || q.includes('car')) { navigate('/fleet'); return respond('🚗 Opening Fleet Manager.'); }
            if (q.includes('staff')) { navigate('/staff'); return respond('👥 Opening Staff Management.'); }
            if (q.includes('contract')) { navigate('/contracts'); return respond('📝 Opening Contracts.'); }
            if (q.includes('invoices') || q.includes('salik')) { navigate('/invoices'); return respond('💸 Opening Invoices & Salik.'); }
            if (q.includes('report') || q.includes('history')) { navigate('/report-history'); return respond('📊 Opening Reports.'); }
            if (q.includes('petty') || q.includes('cash')) { navigate('/custodies'); return respond('💰 Opening Petty Cash.'); }
            if (q.includes('engine')) { navigate('/the-engine'); return respond('⚙️ Start your engines! Opening The Engine.'); }
        }

        // --- 3. Stats & Chit-chat ---
        if (isGreeting(q)) return respond(`Hello ${currentUser?.name?.split(' ')[0] || 'Boss'}! 🦅`);

        if (canAsk) return { text: '', type: 'engine', options: [] };
        return respond("🤔 Try 'Find car 123', 'History 123', or just '123' to choose.");
    };

    return { processQuery, askEngine, canAsk };
};
